/**
 * Meta Pixel (Facebook Pixel) integration for DPEWizard
 * Tracking user behavior and conversions for Meta Ads
 */

// Types for Meta Pixel events
export type MetaPixelEvent = 
  | 'PageView'
  | 'ViewContent'
  | 'Search'
  | 'AddToCart'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'Lead'
  | 'CompleteRegistration'
  | 'Contact'
  | 'CustomizeProduct'
  | 'Donate'
  | 'FindLocation'
  | 'Schedule'
  | 'StartTrial'
  | 'SubmitApplication'
  | 'Subscribe';

export interface MetaPixelEventData {
  content_name?: string;
  content_category?: string;
  content_ids?: string[];
  contents?: Array<{
    id: string;
    quantity: number;
    item_price?: number;
  }>;
  currency?: string;
  value?: number;
  search_string?: string;
  custom_data?: Record<string, any>;
}

// Meta Pixel configuration
const META_PIXEL_ID = '1459460421849683';

// Main Meta Pixel class
export class MetaPixel {
  private static instance: MetaPixel;
  private initialized: boolean = false;
  private debugMode: boolean = false;
  private consentGiven: boolean = true;

  private constructor() {}

  static getInstance(): MetaPixel {
    if (!MetaPixel.instance) {
      MetaPixel.instance = new MetaPixel();
    }
    return MetaPixel.instance;
  }

  /**
   * Initialize Meta Pixel
   */
  init(options: { debugMode?: boolean; consentGiven?: boolean } = {}): void {
    if (this.initialized) return;
    
    this.debugMode = options.debugMode || process.env.NODE_ENV === 'development';
    this.consentGiven = options.consentGiven ?? true;
    
    if (!this.consentGiven) {
      console.log('[Meta Pixel] Tracking disabled - no consent given');
      return;
    }

    try {
      // Load Meta Pixel script
      this.loadPixelScript();
      
      // Initialize pixel
      this.initializePixel();
      
      this.initialized = true;
      
      if (this.debugMode) {
        console.log('[Meta Pixel] Meta Pixel initialized with ID:', META_PIXEL_ID);
      }
    } catch (error) {
      console.error('[Meta Pixel] Failed to initialize Meta Pixel:', error);
    }
  }

  /**
   * Load Meta Pixel script
   */
  private loadPixelScript(): void {
    // Check if script already exists
    if (document.querySelector(`script[src*="connect.facebook.net"]`)) {
      return;
    }

    // Initialize fbq function
    if (!window.fbq) {
      window.fbq = function() {
        if (window.fbq.callMethod) {
          window.fbq.callMethod.apply(window.fbq, arguments);
        } else {
          window.fbq.queue.push(arguments);
        }
      };
      
      if (!window._fbq) {
        window._fbq = window.fbq;
      }
      
      window.fbq.push = window.fbq;
      window.fbq.loaded = true;
      window.fbq.version = '2.0';
      window.fbq.queue = [];
    }

    // Create and load pixel script
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);

    // Add noscript fallback
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1;
    img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.head.appendChild(noscript);
  }

  /**
   * Initialize Meta Pixel
   */
  private initializePixel(): void {
    if (typeof window.fbq !== 'function') {
      console.error('[Meta Pixel] fbq function not available');
      return;
    }

    // Initialize pixel
    window.fbq('init', META_PIXEL_ID);
    
    // Track initial page view
    window.fbq('track', 'PageView');

    if (this.debugMode) {
      console.log('[Meta Pixel] Pixel initialized and PageView tracked');
    }
  }

  /**
   * Track custom event
   */
  trackEvent(event: MetaPixelEvent, eventData: MetaPixelEventData = {}): void {
    if (!this.initialized || !this.consentGiven) return;
    
    if (typeof window.fbq === 'function') {
      // Clean event data - remove undefined values
      const cleanEventData = Object.entries(eventData).reduce((acc, [key, value]) => {
        if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, any>);
      
      window.fbq('track', event, cleanEventData);
      
      if (this.debugMode) {
        console.log('[Meta Pixel] Event tracked:', event, cleanEventData);
      }
    }
  }

  /**
   * Track page view
   */
  trackPageView(pageName?: string): void {
    this.trackEvent('PageView', {
      content_name: pageName,
      content_category: 'page'
    });
  }

  /**
   * Track user registration
   */
  trackRegistration(userId: string): void {
    this.trackEvent('CompleteRegistration');
  }

  /**
   * Track user login
   */
  trackLogin(): void {
    this.trackEvent('ViewContent', {
      content_name: 'User Login',
      content_category: 'auth'
    });
  }

  /**
   * Track DPE analysis start
   */
  trackAnalysisStart(analysisId: string, analysisType: string = 'dpe'): void {
    this.trackEvent('InitiateCheckout', {
      content_name: 'DPE Analysis Start',
      content_category: 'analysis',
      content_ids: [analysisId],
      custom_data: {
        analysis_id: analysisId,
        analysis_type: analysisType
      }
    });
  }

  /**
   * Track DPE analysis completion
   */
  trackAnalysisComplete(analysisId: string, energyClass: string, success: boolean): void {
    this.trackEvent('Purchase', {
      content_name: 'DPE Analysis Complete',
      content_category: 'analysis',
      content_ids: [analysisId],
      value: success ? 1 : 0,
      currency: 'EUR',
      custom_data: {
        analysis_id: analysisId,
        energy_class: energyClass,
        success: success
      }
    });
  }

  /**
   * Track pricing CTA clicks (Analyser mon DPE maintenant / Commencer)
   */
  trackPricingCTA(): void {
    this.trackEvent('Lead', {
      content_name: 'DPE Analysis Interest',
      content_category: 'prospect'
    });
  }

  /**
   * Track DPE purchase (unified for credits and subscriptions) 
   */
  trackPurchaseComplete(amount: number, purchaseType: 'credit' | 'subscription', currency: string = 'EUR'): void {
    this.trackEvent('Purchase', {
      content_name: purchaseType === 'subscription' ? 'DPE Subscription Purchase' : 'DPE Credit Purchase',
      content_category: 'purchase',
      value: amount,
      currency: currency,
      custom_data: {
        purchase_type: purchaseType
      }
    });
  }

  /**
   * Track subscription purchase
   */
  trackSubscription(planId: string, amount: number, currency: string = 'EUR'): void {
    this.trackEvent('InitiateCheckout', {
      content_name: 'DPE Subscription Selection',
      content_category: 'subscription',
      value: amount,
      currency: currency,
      custom_data: {
        plan_id: planId
      }
    });
  }

  /**
   * Track credit purchase
   */
  trackCreditPurchase(creditsAmount: number, amount: number, currency: string = 'EUR'): void {
    this.trackEvent('InitiateCheckout', {
      content_name: 'DPE Credit Selection',
      content_category: 'credits',
      value: amount,
      currency: currency,
      contents: [{
        id: 'dpe_credits',
        quantity: creditsAmount,
        item_price: amount / creditsAmount
      }],
      custom_data: {
        credits_amount: creditsAmount
      }
    });
  }

  /**
   * Track payment abandonment
   */
  trackPaymentAbandoned(amount?: number, purchaseType?: 'credit' | 'subscription', reason: string = 'user_cancelled'): void {
    this.trackEvent('AddToCart', {
      content_name: 'Payment Abandoned',
      content_category: 'abandonment',
      value: amount,
      currency: 'EUR',
      custom_data: {
        purchase_type: purchaseType,
        abandonment_reason: reason
      }
    });
  }

  /**
   * Track search events
   */
  trackSearch(searchTerm: string, searchType: string = 'dpe'): void {
    this.trackEvent('Search', {
      content_category: searchType,
      search_string: searchTerm,
      custom_data: {
        search_type: searchType
      }
    });
  }

  /**
   * Track contact form submission
   */
  trackContact(contactType: string = 'contact_form'): void {
    this.trackEvent('Contact', {
      content_name: 'Contact Form',
      content_category: 'contact',
      custom_data: {
        contact_type: contactType
      }
    });
  }

  /**
   * Track pricing page view
   */
  trackPricingView(): void {
    this.trackEvent('ViewContent', {
      content_name: 'Pricing Page',
      content_category: 'pricing'
    });
  }

  /**
   * Track custom DPE events
   */
  trackDPEUpload(dpeId: string, fileSize?: number): void {
    this.trackEvent('AddToCart', {
      content_name: 'DPE Upload',
      content_category: 'dpe',
      content_ids: [dpeId],
      custom_data: {
        dpe_id: dpeId,
        file_size: fileSize
      }
    });
  }

  /**
   * Track questionnaire completion
   */
  trackQuestionnaireComplete(sessionId: string, questionsAnswered: number): void {
    this.trackEvent('CompleteRegistration', {
      content_name: 'Questionnaire Complete',
      content_category: 'questionnaire',
      custom_data: {
        session_id: sessionId,
        questions_answered: questionsAnswered
      }
    });
  }

  /**
   * Update consent status
   */
  updateConsent(consentGiven: boolean): void {
    this.consentGiven = consentGiven;
    
    if (!consentGiven && this.initialized) {
      // Disable tracking
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'revoke');
      }
    } else if (consentGiven && !this.initialized) {
      // Re-initialize if consent is given
      this.init({ consentGiven: true, debugMode: this.debugMode });
    } else if (consentGiven && this.initialized) {
      // Grant consent
      if (typeof window.fbq === 'function') {
        window.fbq('consent', 'grant');
      }
    }
  }
}

// Export singleton instance
export const metaPixel = MetaPixel.getInstance();

// Global type declarations
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
    _fbq: (...args: any[]) => void;
  }
}

export default metaPixel;